import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PackageSearch, ShoppingCart, Trash2, Undo2 } from "lucide-react";
import {
  api,
  type AnalyticsPeriod,
  type AnalyticsProducts,
  type Product,
  type ProductSignalKind,
  type ProductSignalRow,
  type ProductSignals,
} from "../api";
import { fmtDate, fmtMoney, fmtQty } from "../lib/format";
import { InfoTooltip } from "../components/info-tooltip";
import { MiniKpi } from "../components/mini-kpi";
import { SignalCards } from "../components/signal-cards";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { EmptyState } from "../components/ui/empty-state";
import { StatusChip } from "../components/ui/ledger";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { useBranch } from "../lib/use-branch";
import { cn } from "../lib/utils";
import type { OmborJump } from "./OmborPage";

/** Tovar sotuvi jadvalining saralash o'qi. */
type ProductSort = "revenue" | "profit" | "margin";

/** Ro'yxatda ko'rsatiladigan guruhlar — `yangi` ATAYLAB yo'q: u hukm emas. */
const PILLS: ProductSignalKind[] = [
  "qotgan",
  "tugayotgan",
  "sekin",
  "yaxshi",
  "belgisiz",
];

/** Signal → chip toni. Qotgan pul eng qimmat muammo, shuning uchun `danger`. */
const TONES: Record<ProductSignalKind, "ok" | "warn" | "danger" | "muted"> = {
  qotgan: "danger",
  tugayotgan: "warn",
  sekin: "warn",
  yaxshi: "ok",
  belgisiz: "muted",
  yangi: "muted",
};

/**
 * Tovar tahlili — sotuv reytingi (davr bo'yicha) + ombor signallari.
 *
 * Yuqorida SOTUV bo'limi: sahifa sarlavhasidagi davr tanloviga bo'ysunadigan
 * tovar-sotuv jadvali (`/owner/analytics/products`, sotuv + ishga biriktirilgan
 * chiqimlar — KPI karusel bilan bir xil qamrov). Pastda SIGNALLAR bo'limi —
 * o'zining qat'iy 180 kunlik oynasi bilan, davr tanloviga bog'lanmaydi
 * (`signallar.intro` shuni aytadi).
 *
 * Signallar TERGOV asbobi emas, ISH RO'YXATI: har qator bitta amalga olib
 * boradi. Shuning uchun saralanadigan ustunlar yo'q — server allaqachon eng
 * qimmat muammoni tepaga qo'yadi (qotganda bog'langan pul bo'yicha,
 * tugayotganda tovar keltirgan tushum bo'yicha), pill esa guruhni almashtiradi.
 *
 * Tahlil sahifasining ichki tabi sifatida chiziladi. Tarif qorovuli
 * takrorlanmaydi: butun Tahlil sahifasi `<FeatureGate feature="analytics">`
 * ichida, bu tab esa `tovars` feature'siz umuman chizilmaydi. Qator amallari
 * Ombor sahifasiga tushadi — payload `onJump` orqali App'ga ko'tariladi.
 */
export function TovarTahliliPage({
  period,
  onJump,
}: {
  /** Sahifa sarlavhasidagi davr tanlovi — sotuv bo'limi shu davrni oladi. */
  period: AnalyticsPeriod;
  /** Qator amali — Ombor sahifasiga mos tab + payload bilan o'tish. */
  onJump: (jump: OmborJump) => void;
}) {
  const { t } = useTranslation(["analytics", "common"]);
  const { selectedBranchId } = useBranch();
  const [pill, setPill] = useState<ProductSignalKind>("qotgan");
  const [data, setData] = useState<ProductSignals | null>(null);
  // Xarid formasiga to'liq `Product` kerak (faqat id emas), `api.ts` da esa
  // bitta tovarni oladigan metod yo'q — katalog baribir sahifalanmaydi va
  // Tovarlar tabi ham aynan shuni yuklaydi.
  const [catalog, setCatalog] = useState<Map<number, Product>>(new Map());
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [sales, setSales] = useState<AnalyticsProducts | null>(null);
  const [salesLoading, setSalesLoading] = useState(true);
  const [salesErr, setSalesErr] = useState<string | null>(null);
  // "Nima foydali?" savoli tushumga qarab javob bermaydi — shuning uchun
  // saralash. Bu FAQAT ko'rinish: server allaqachon yuborgan qatorlar qayta
  // tartiblanadi, yangi so'rov ham, foydaning yangi ta'rifi ham yo'q.
  const [sortBy, setSortBy] = useState<ProductSort>("revenue");

  // Sotuv bo'limi ALOHIDA effect'da: davr almashganda signallar jadvali
  // qayta so'ralib "lip-lip" qilmasin (signallar davrga bog'liq emas).
  useEffect(() => {
    setSalesLoading(true);
    api
      .analyticsProducts(period, selectedBranchId)
      .then((res) => {
        setSales(res);
        setSalesErr(null);
      })
      .catch((e) =>
        setSalesErr((e as { message?: string }).message || t("common:common.error")),
      )
      .finally(() => setSalesLoading(false));
  }, [period, selectedBranchId, t]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [signals, products] = await Promise.all([
          api.productSignals({
            branch_id: selectedBranchId ?? undefined,
            signal: pill,
            limit: 200,
          }),
          api.products({ branch_id: selectedBranchId ?? undefined }),
        ]);
        setData(signals);
        setCatalog(new Map(products.map((p) => [p.id, p])));
        setErr(null);
      } catch (e) {
        setErr((e as { message?: string }).message || t("common:common.error"));
      } finally {
        setLoading(false);
      }
    })();
  }, [pill, selectedBranchId, t]);

  /**
   * Saralangan tovar qatorlari. `share_pct` va jami ustunlar SERVERDAN
   * keladi va o'zgarmaydi — bu yerda faqat tartib almashadi.
   *
   * CHEGARA: server ro'yxatni TUSHUM bo'yicha kesadi (`limit`, standart 50),
   * shuning uchun "Foyda"/"Marja" shu kesimni qayta tartiblaydi, undan
   * tashqaridagi yuqori marjali tovarni OLIB KELMAYDI. Buni yashirmaymiz —
   * `scopeHint` ochiq aytadi; chinakam foyda reytingi backendda saralashni
   * talab qiladi.
   */
  const salesRows = useMemo(() => {
    const items = sales?.items ?? [];
    if (sortBy === "revenue") return items; // server tartibi
    return [...items].sort((a, b) => {
      if (sortBy === "profit") return Number(b.profit) - Number(a.profit);
      // Marja aniqlanmagan tovar (tushum 0) doim oxirida.
      return (b.margin_pct ?? -Infinity) - (a.margin_pct ?? -Infinity);
    });
  }, [sales, sortBy]);

  const rows = data?.items ?? [];

  /** Guruh bo'yicha sanoq — pill yorlig'idagi raqam yuklanishdan keyin chiqadi. */
  const counts = useMemo(() => {
    if (!data) return null;
    return {
      qotgan: data.qotgan.soni,
      tugayotgan: data.tugayotgan.soni,
      sekin: data.sekin.soni,
      yaxshi: data.yaxshi.soni,
      belgisiz: data.belgisiz.soni,
    } as Record<ProductSignalKind, number>;
  }, [data]);

  function actionFor(row: ProductSignalRow) {
    // Tugayotgan → darhol buyurtma qilish. Katalogda tovar topilmasa (kesh
    // eskirgan bo'lsa) tugma umuman chizilmaydi — hech narsa qilmaydigan
    // tugmadan ko'ra yo'qligi tushunarli.
    if (row.signal === "tugayotgan") {
      const product = catalog.get(row.product_id);
      if (!product) return null;
      return (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={() => onJump({ kind: "xarid", product })}
        >
          <ShoppingCart />
          {t("signallar.actions.buy")}
        </Button>
      );
    }
    // Qotgan (o'lik zaxira) → ASOSIY amal HISOBDAN CHIQARISH. Oylar oldin
    // olingan ehtiyot qismni yetkazib beruvchi odatda qaytarib olmaydi, ya'ni
    // qaytarish — kamdan-kam ishlaydigan omad, hisobdan chiqarish esa har doim
    // mavjud haqiqiy yechim: zarar tan olinadi va ombor raqami rostga aylanadi.
    // Yetkazib beruvchiga qaytarish IKKILAMCHI tugma bo'lib qoladi (mumkin
    // bo'lgan holat yo'qolmasin), `tuzatish` bilan kirgan zaxirada esa xarid
    // hujjati YO'Q — o'sha tugma sababi bilan o'chirilgan holda ko'rsatiladi.
    if (row.signal === "qotgan") {
      const product = catalog.get(row.product_id);
      const noDoc = row.oxirgi_xarid_id == null;
      return (
        <div className="flex items-center justify-end gap-1">
          {product && (
            <Button
              variant="secondary"
              size="sm"
              className="h-7 text-xs"
              onClick={() => onJump({ kind: "chiqim", product })}
            >
              <Trash2 />
              {t("signallar.actions.writeOff")}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            disabled={noDoc}
            title={noDoc ? t("signallar.actions.noPurchaseDoc") : undefined}
            onClick={() =>
              row.oxirgi_xarid_id != null &&
              onJump({ kind: "purchaseDoc", docId: row.oxirgi_xarid_id })
            }
          >
            <Undo2 />
            {t("signallar.actions.return")}
          </Button>
        </div>
      );
    }
    // Sekin sotilayotgan tovar hali SOTILMOQDA — uni hisobdan chiqarish
    // sotiladigan zaxirani yo'q qilish bo'lardi, shuning uchun bu yerda faqat
    // yetkazib beruvchiga qaytarish taklif qilinadi.
    if (row.signal === "sekin") {
      const disabled = row.oxirgi_xarid_id == null;
      return (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          disabled={disabled}
          title={disabled ? t("signallar.actions.noPurchaseDoc") : undefined}
          onClick={() =>
            row.oxirgi_xarid_id != null &&
            onJump({ kind: "purchaseDoc", docId: row.oxirgi_xarid_id })
          }
        >
          <Undo2 />
          {t("signallar.actions.return")}
        </Button>
      );
    }
    return null;
  }

  return (
    <div className="space-y-4">
      {/* ===== Sotuv bo'limi — sahifa davr tanloviga bo'ysunadi ===== */}
      {salesErr && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3 text-sm">
          {salesErr}
        </div>
      )}

      {salesLoading && !sales ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[92px]" />
          ))}
        </div>
      ) : sales ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <MiniKpi
            label={t("productSales.kpi.qty")}
            value={fmtQty(sales.totals.qty)}
          />
          <MiniKpi
            label={t("productSales.kpi.revenue")}
            value={fmtMoney(sales.totals.revenue)}
          />
          <MiniKpi
            label={t("productSales.kpi.profit")}
            value={fmtMoney(sales.totals.profit)}
            hint={<InfoTooltip text={t("productSales.kpi.profitHint")} />}
          />
          <MiniKpi
            label={t("productSales.kpi.margin")}
            value={
              sales.totals.margin_pct != null
                ? `${sales.totals.margin_pct.toFixed(1)}%`
                : "—"
            }
          />
        </div>
      ) : null}

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <CardTitle className="text-base">
                {t("productSales.title")}{" "}
                <InfoTooltip text={t("productSales.scopeHint")} />
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                {t("productSales.subtitle")}
              </p>
            </div>
            <Tabs value={sortBy} onValueChange={(v) => setSortBy(v as ProductSort)}>
              <TabsList className="overflow-x-auto">
                <TabsTrigger value="revenue">{t("productSales.sortRevenue")}</TabsTrigger>
                <TabsTrigger value="profit">{t("productSales.sortProfit")}</TabsTrigger>
                <TabsTrigger value="margin">{t("productSales.sortMargin")}</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {salesLoading && !sales ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          ) : !sales || sales.items.length === 0 ? (
            <EmptyState title={t("empty.noSales")} className="py-8" />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("productSales.columns.product")}</TableHead>
                    <TableHead className="text-right">
                      {t("productSales.columns.qty")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("productSales.columns.revenue")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("productSales.columns.profit")}
                      <InfoTooltip
                        className="ml-1"
                        align="end"
                        text={t("productSales.kpi.profitHint")}
                      />
                    </TableHead>
                    <TableHead className="text-right">
                      {t("productSales.columns.margin")}
                    </TableHead>
                    {/* Ulush ikkinchi darajali — telefonda ustun yeydi. */}
                    <TableHead className="text-right hidden md:table-cell">
                      {t("productSales.columns.share")}
                      <InfoTooltip
                        className="ml-1"
                        align="end"
                        text={t("productSales.columns.shareHint")}
                      />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {salesRows.map((p) => (
                    <TableRow key={p.product_id}>
                      <TableCell>
                        <div className="font-medium">{p.nomi}</div>
                        {p.kategoriya && (
                          <div className="text-xs text-muted-foreground">
                            {p.kategoriya}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmtQty(p.qty)} {p.birlik}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmtMoney(p.revenue)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmtMoney(p.profit)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {p.margin_pct != null ? `${p.margin_pct.toFixed(1)}%` : "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums hidden md:table-cell">
                        {p.share_pct.toFixed(1)}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell>{t("productSales.totals")}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtQty(sales.totals.qty)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtMoney(sales.totals.revenue)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtMoney(sales.totals.profit)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {sales.totals.margin_pct != null
                        ? `${sales.totals.margin_pct.toFixed(1)}%`
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums hidden md:table-cell">
                      100%
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
              {sales.items.length < sales.totals.products_count && (
                <p className="px-4 py-2 text-xs text-muted-foreground border-t">
                  {t("productSales.shownOf", {
                    shown: sales.items.length,
                    total: sales.totals.products_count,
                  })}
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ===== Signallar bo'limi — o'z 180 kunlik oynasi, davrga bog'lanmaydi ===== */}
      <h3 className="text-sm font-semibold">{t("signallar.sectionTitle")}</h3>

      {err && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3 text-sm">
          {err}
        </div>
      )}

      {/* Ikkala karta shu javobning o'zidan chiziladi (`signal` filtri faqat
          `items`ni cheklaydi, guruh yig'indilari doim to'liq keladi) — qo'shimcha
          so'rov yo'q. Pill almashganda eski `data` saqlanib turadi, shuning uchun
          kartalar skeletga qaytib "lip-lip" qilmaydi. */}
      <SignalCards
        data={data}
        loading={loading && !data}
        onOpen={(signal) => setPill(signal)}
      />

      <p className="text-sm text-muted-foreground">
        {t("signallar.intro", { days: data?.window_days ?? 180 })}
      </p>

      {/* Guruh izohi pill qatorining YONIDA bitta ⓘ: InfoTooltip o'zi <button>,
          uni TabsTrigger (ham <button>) ichiga qo'yib bo'lmaydi. Ichki chegara
          raqamlari (180 kun, qoplash ≥ …) bu izohlarda ATAYLAB yo'q — foydalanuvchi
          "nega?" desa, oddiy tildagi javob yetadi. */}
      <div className="flex items-center gap-2">
        <Tabs
          value={pill}
          onValueChange={(v) => setPill(v as ProductSignalKind)}
        >
          <TabsList className="overflow-x-auto">
            {PILLS.map((p) => (
              <TabsTrigger key={p} value={p}>
                {t(`signallar.pills.${p}`)}
                {counts ? ` (${counts[p]})` : ""}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <InfoTooltip text={t(`signallar.signalHint.${pill}`)} />
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<PackageSearch />}
              title={t(`signallar.empty.${pill}.title`)}
              description={
                data && data.baholangan === 0
                  ? t("signallar.empty.noHistory")
                  : t(`signallar.empty.${pill}.desc`)
              }
            />
          ) : (
            <Table>
              {/* Chalkashtirishi mumkin bo'lgan 5 ustunda ⓘ bor; Tovar, Qoldiq
                  va Amal o'z-o'zidan tushunarli — hammasiga qo'yilsa jadval
                  keragidan "texnik" ko'rinadi. O'ngga tekislangan ustunlarda
                  popover chetdan chiqib ketmasin deb `align="end"`. */}
              <TableHeader>
                <TableRow>
                  <TableHead>{t("signallar.table.nomi")}</TableHead>
                  <TableHead className="text-right">
                    {t("signallar.table.ostatka")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("signallar.table.qiymat")}
                    <InfoTooltip
                      className="ml-1"
                      align="end"
                      text={t("signallar.table.qiymatHint")}
                    />
                  </TableHead>
                  <TableHead className="text-right">
                    {t("signallar.table.qoplash")}
                    <InfoTooltip
                      className="ml-1"
                      align="end"
                      text={t("signallar.table.qoplashHint")}
                    />
                  </TableHead>
                  <TableHead className="text-right">
                    {t("signallar.table.sotilgan")}
                    <InfoTooltip
                      className="ml-1"
                      align="end"
                      text={t("signallar.table.sotilganHint", {
                        days: data?.window_days ?? 180,
                      })}
                    />
                  </TableHead>
                  {/* Sana va ombordagi yosh — ikkinchi darajali: 8 ustun
                      1280px noutbukda sig'masdi. Amal ustuni QOLADI (bu
                      jadval ish ro'yxati), skroll esa kartaning ichida. */}
                  <TableHead className="hidden lg:table-cell">
                    {t("signallar.table.oxirgiSotuv")}
                    <InfoTooltip
                      className="ml-1"
                      text={t("signallar.table.oxirgiSotuvHint", {
                        days: data?.window_days ?? 180,
                      })}
                    />
                  </TableHead>
                  <TableHead className="text-right hidden lg:table-cell">
                    {t("signallar.table.zaxiraKuni")}
                    <InfoTooltip
                      className="ml-1"
                      align="end"
                      text={t("signallar.table.zaxiraKuniHint")}
                    />
                  </TableHead>
                  <TableHead className="text-right">
                    {t("signallar.table.amallar")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.product_id}
                    className={cn(!row.faol && "opacity-60")}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{row.nomi}</span>
                        <StatusChip tone={TONES[row.signal]}>
                          {t(`signallar.signal.${row.signal}`)}
                        </StatusChip>
                        {!row.faol && (
                          <StatusChip tone="muted" dot={false}>
                            {t("signallar.inactive")}
                          </StatusChip>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtQty(row.on_hand)} {row.birlik}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {Number(row.tannarx) > 0 ? (
                        fmtMoney(row.qiymat)
                      ) : (
                        <span
                          className="text-muted-foreground"
                          title={t("signallar.table.noCostHint")}
                        >
                          —
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {row.qoplash_kuni == null
                        ? t("signallar.table.neverSold")
                        : t("signallar.table.coverDays", {
                            days: Math.round(row.qoplash_kuni),
                          })}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtQty(row.sotilgan_miqdor)}
                    </TableCell>
                    <TableCell className="text-muted-foreground hidden lg:table-cell">
                      {row.oxirgi_sotuv_at ? fmtDate(row.oxirgi_sotuv_at) : "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums hidden lg:table-cell">
                      {t("signallar.table.stockDays", { days: row.zaxira_kuni })}
                    </TableCell>
                    <TableCell className="text-right">{actionFor(row)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
